import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Link2,
  Inbox,
  Send,
  Loader2,
  CheckCircle,
  XCircle,
  Shield,
  GitBranch
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ConnectionRequests = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [sent, setSent] = useState([]);
  const [received, setReceived] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [processingId, setProcessingId] = useState(null);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/discovery/connection-requests`);
      setSent(response.data.sent || []);
      setReceived(response.data.received || []);
      setPendingCount(response.data.pending_count || 0);
    } catch (error) {
      console.error('Error fetching connection requests:', error);
      toast.error('Unable to load connection requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const handleAccept = async (requestId) => {
    setProcessingId(requestId);
    try {
      await axios.post(`${API}/discovery/connection-requests/${requestId}/accept`);
      toast.success('Connection request accepted');
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Unable to accept request');
    } finally {
      setProcessingId(null);
    }
  };

  const handleDecline = async (requestId) => {
    setProcessingId(requestId);
    try {
      await axios.post(`${API}/discovery/connection-requests/${requestId}/decline`);
      toast.success('Connection request declined');
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Unable to decline request');
    } finally {
      setProcessingId(null);
    }
  };

  const statusBadge = (status) => {
    const styles = {
      pending: 'border border-amber-500/35 bg-amber-500/10 text-amber-800',
      accepted: 'border border-[#c9a961]/35 bg-[#c9a961]/12 text-[#5c4a32]',
      rejected: 'border border-[#e8e2d8] bg-[#ebe6dc]/80 text-[#68645e]'
    };
    const labels = { pending: 'Pending', accepted: 'Accepted', rejected: 'Declined' };
    return (
      <span className={`rounded-full px-2 py-0.5 text-xs ${styles[status] || styles.rejected}`}>
        {labels[status] || status}
      </span>
    );
  };

  const RequestCard = ({ req, isReceived, onAccept, onDecline }) => (
    <Card className="border-[#e8e2d8] bg-[#fffcf7]/90 shadow-sm">
      <CardContent className="space-y-3 p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#ebe6dc]/90 ring-1 ring-[#e8e2d8]">
              <GitBranch className="h-4 w-4 text-[#5c534a]" aria-hidden />
            </div>
            <div>
              <p className="text-sm font-medium text-[#1c1917]">
                {isReceived ? req.requester_name : req.recipient_name}
              </p>
              <p className="text-xs text-[#68645e]">
                {isReceived ? req.requester_tree_name : req.recipient_tree_name}
              </p>
            </div>
          </div>
          {statusBadge(req.status)}
        </div>
        {req.message && (
          <p className="border-l-2 border-[#c9a961]/35 pl-3 text-xs italic text-[#5c534a]">
            &ldquo;{req.message}&rdquo;
          </p>
        )}
        <p className="text-[10px] text-[#8b7355]">
          {new Date(req.created_at).toLocaleDateString()}
        </p>
        {isReceived && req.status === 'pending' && (
          <div className="flex gap-2 pt-1">
            <Button
              size="sm"
              onClick={() => onAccept(req.id)}
              disabled={processingId === req.id}
              className="rounded-full bg-[#1c1917] text-[#faf7f2] hover:bg-[#2c2824]"
            >
              {processingId === req.id ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <CheckCircle className="mr-1 h-3 w-3" />
              )}
              Accept
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => onDecline(req.id)}
              disabled={processingId === req.id}
              className="rounded-full border-[#c9a961]/40 bg-transparent text-[#1c1917] hover:bg-[#faf8f5]"
            >
              <XCircle className="mr-1 h-3 w-3" />
              Decline
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-[#faf8f5]" data-testid="connection-requests-page">
      <nav className="sticky top-0 z-50 border-b border-[#e8e2d8]/90 bg-[#faf8f5]/95 backdrop-blur-sm">
        <div className="mx-auto max-w-4xl px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/dashboard')}
                className="rounded-full text-[#5c534a] hover:bg-[#ebe6dc]/60 hover:text-[#1c1917]"
                data-testid="back-btn"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="font-serif text-xl font-semibold text-[#1c1917]">Connection Requests</h1>
                <p className="text-xs text-[#68645e]">
                  Connect with other researchers based on family matches
                </p>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-6 flex items-start gap-2.5 rounded-2xl border border-[#e8e2d8] bg-[#fffcf7]/90 p-3.5 shadow-sm">
          <Shield className="mt-0.5 h-4 w-4 shrink-0 text-[#8b7355]" aria-hidden />
          <p className="text-xs leading-relaxed text-[#5c534a]">
            Connection requests are sent when you find a potential family match in another researcher&apos;s tree. No
            personal contact details are shared until both parties accept.
          </p>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="mb-3 h-8 w-8 animate-spin text-[#8b7355]" />
            <p className="text-sm text-[#68645e]">Loading connection requests...</p>
          </div>
        ) : (
          <Tabs defaultValue="received" className="space-y-4">
            <TabsList className="inline-flex h-auto min-h-9 w-full flex-wrap items-center justify-start gap-1 rounded-xl border border-[#e8e2d8] bg-[#ebe6dc]/50 p-1 sm:w-auto sm:flex-nowrap">
              <TabsTrigger
                value="received"
                className="rounded-lg data-[state=active]:bg-[#fffcf7] data-[state=active]:text-[#1c1917] data-[state=active]:shadow-sm"
              >
                <Inbox className="mr-2 h-4 w-4" />
                Received
                {pendingCount > 0 && (
                  <span className="ml-2 rounded-full bg-[#c9a961]/20 px-1.5 py-0.5 text-xs text-[#5c4a32]">
                    {pendingCount}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger
                value="sent"
                className="rounded-lg data-[state=active]:bg-[#fffcf7] data-[state=active]:text-[#1c1917] data-[state=active]:shadow-sm"
              >
                <Send className="mr-2 h-4 w-4" />
                Sent
              </TabsTrigger>
            </TabsList>
            <TabsContent value="received" className="space-y-3">
              {received.length === 0 ? (
                <Card className="border-[#e8e2d8] bg-[#fffcf7]/90">
                  <CardContent className="py-12 text-center">
                    <Link2 className="mx-auto mb-3 h-12 w-12 text-[#c9a961]/60" aria-hidden />
                    <p className="text-sm text-[#68645e]">No connection requests received yet</p>
                    <p className="mt-1 text-xs text-[#8b7355]">
                      When other researchers find potential matches with your tree, they can send you a request here.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                received.map((req) => (
                  <RequestCard
                    key={req.id}
                    req={req}
                    isReceived={true}
                    onAccept={handleAccept}
                    onDecline={handleDecline}
                  />
                ))
              )}
            </TabsContent>
            <TabsContent value="sent" className="space-y-3">
              {sent.length === 0 ? (
                <Card className="border-[#e8e2d8] bg-[#fffcf7]/90">
                  <CardContent className="py-12 text-center">
                    <Send className="mx-auto mb-3 h-12 w-12 text-[#c9a961]/60" aria-hidden />
                    <p className="text-sm text-[#68645e]">No connection requests sent yet</p>
                    <p className="mt-1 text-xs text-[#8b7355]">
                      Send requests from Cross-Tree Matches when viewing a tree with discovery enabled.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                sent.map((req) => (
                  <RequestCard
                    key={req.id}
                    req={req}
                    isReceived={false}
                    onAccept={() => {}}
                    onDecline={() => {}}
                  />
                ))
              )}
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
};

export default ConnectionRequests;
